import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, useSpring, useReducedMotion, useMotionTemplate } from 'framer-motion';

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const LiquidProgressBar = ({
  value,
  max,
  progressEvent,
  onAnimationComplete,
  heightClass = 'h-2',
}) => {
  const shouldReduce = useReducedMotion();
  const [flash, setFlash] = useState(false);
  const isAnimatingRef = useRef(false);

  // Determine current bounds based on event or static
  const { fromPercent, toPercent, leveledUp } = useMemo(() => {
    const basePercent = max > 0 ? (value / max) * 100 : 0;
    if (!progressEvent) {
      return {
        fromPercent: clampPercent(basePercent),
        toPercent: clampPercent(basePercent),
        leveledUp: false,
      };
    }

    const { from = {}, to = {}, leveledUp: eventLeveledUp } = progressEvent;
    const start = from.xpToNextLevel > 0 ? (from.xp / from.xpToNextLevel) * 100 : 0;
    const end = to.xpToNextLevel > 0 ? (to.xp / to.xpToNextLevel) * 100 : 0;

    return {
      fromPercent: clampPercent(start),
      toPercent: clampPercent(end),
      leveledUp: Boolean(eventLeveledUp),
    };
  }, [value, max, progressEvent]);

  // Unified visual progress spring
  const springProgress = useSpring(fromPercent, {
    stiffness: 45,
    damping: 15,
    mass: 1,
    restDelta: 0.1,
  });

  // Animated template strings for styles
  const widthTemplate = useMotionTemplate`${springProgress}%`;
  const leftTemplate = useMotionTemplate`calc(${springProgress}% - 6px)`;

  useEffect(() => {
    let isActive = true;

    const runAnimation = async () => {
      if (shouldReduce) {
        springProgress.set(toPercent);
        onAnimationComplete?.();
        return;
      }

      if (!progressEvent) {
        springProgress.set(toPercent);
        return;
      }

      if (isAnimatingRef.current) return;
      isAnimatingRef.current = true;

      // Force spring to start exactly at fromPercent without animation
      springProgress.jump(fromPercent);

      // Async wrapper to await spring settlement
      const waitForSpring = (target) => new Promise((resolve) => {
        const unsubscribe = springProgress.on("change", (latest) => {
          if (Math.abs(latest - target) < 0.5 || springProgress.getVelocity() === 0) {
            unsubscribe();
            setTimeout(resolve, 50); // slight buffer
          }
        });
        springProgress.set(target);
      });

      if (leveledUp && fromPercent < 100) {
        // Fill to 100%
        await waitForSpring(100);

        if (!isActive) return;
        setFlash(true);
        setTimeout(() => { if (isActive) setFlash(false); }, 250);

        // Instantly drop to 0
        springProgress.jump(0);

        // Fill to final
        await waitForSpring(toPercent);
      } else {
        // Standard fill
        await waitForSpring(toPercent);
      }

      if (isActive) {
        isAnimatingRef.current = false;
        onAnimationComplete?.();
      }
    };

    runAnimation();

    return () => {
      isActive = false;
    };
  }, [progressEvent, fromPercent, toPercent, leveledUp, springProgress, shouldReduce, onAnimationComplete]);

  return (
    <div className={`w-full bg-slate-900/80 rounded-full relative overflow-hidden ${heightClass} shadow-inner border border-white/5`}>
      {/* Trough Depth (Inner Shadow) */}
      <div className="absolute inset-0 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)] rounded-full pointer-events-none z-10" />

      {/* The Liquid Fill (Width driven by Spring) */}
      <motion.div
        className="absolute inset-y-0 left-0 rounded-full overflow-hidden"
        style={{ width: widthTemplate, boxShadow: '0 0 15px rgba(6,182,212,0.5)' }}
      >
        {/* Animated Flowing Gradient */}
        <motion.div
          className="w-full h-full bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600"
          style={{ backgroundSize: "200% 100%" }}
          animate={{ backgroundPosition: ["100% 0%", "-100% 0%"] }}
          transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
        />
        {/* Glass Highlight */}
        <div className="absolute top-0 left-0 right-0 h-[40%] bg-gradient-to-b from-white/30 to-transparent pointer-events-none" />
      </motion.div>

      {/* Leading Edge Meniscus (Left position driven by Spring) */}
      <motion.div
        className="absolute inset-y-0 pointer-events-none z-20 w-[12px]"
        style={{ left: leftTemplate }}
      >
        <div className="w-full h-full bg-cyan-300 rounded-full blur-[4px] opacity-80" />
      </motion.div>

      {/* Level Up Flash */}
      {flash && (
        <div className="absolute inset-0 bg-white/80 animate-pulse pointer-events-none z-30 mix-blend-overlay" />
      )}
    </div>
  );
};

export default LiquidProgressBar;
